import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Send, Copy, Loader2, ShieldAlert, Search } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "harcelement", label: "Harcèlement" },
  { value: "violence", label: "Violence" },
  { value: "discrimination", label: "Discrimination" },
  { value: "corruption", label: "Corruption" },
  { value: "mauvais_comportement", label: "Mauvais comportement" },
  { value: "autre", label: "Autre situation préoccupante" },
];

export default function SignalementSubmitPage() {
  const [form, setForm] = useState({
    category: "harcelement", subject: "", description: "", location: "", reported_person: "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [trackCode, setTrackCode] = useState("");
  const [trackResult, setTrackResult] = useState(null);

  const submit = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error("Le sujet et la description sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/erp/signalements", form);
      setResult(data);
      setForm({ category: "harcelement", subject: "", description: "", location: "", reported_person: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const track = async () => {
    if (!trackCode.trim()) return;
    try {
      const { data } = await api.get(`/erp/signalements/track/${trackCode.trim()}`);
      setTrackResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code introuvable");
      setTrackResult(null);
    }
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c);
    toast.success("Code copié");
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="signalement-submit">
      <div>
        <div className="chip mb-2"><ShieldAlert className="h-3 w-3" /><span>SIGNALEMENT SÉCURISÉ</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Signaler anonymement</h1>
        <p className="text-slate-500 mt-1">
          Signalez toute situation préoccupante (harcèlement, violence, discrimination, corruption…). Votre signalement est <strong>100% anonyme</strong> et transmis uniquement au Fondateur et à la Direction.
        </p>
      </div>

      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">
            <p className="font-semibold mb-1">Garantie d&apos;anonymat</p>
            <p>Aucune information sur votre identité (nom, email, IP, session) n&apos;est enregistrée avec ce signalement. Un code de suivi vous sera fourni pour connaître l&apos;état de traitement.</p>
          </div>
        </div>
      </div>

      {result && (
        <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-5" data-testid="signal-result">
          <div className="text-green-900 font-bold text-lg mb-2">✅ Signalement enregistré</div>
          <p className="text-sm text-green-800 mb-3">{result.message}</p>
          <div className="bg-white border border-green-200 rounded-lg p-3 flex items-center justify-between">
            <div>
              <div className="text-xs text-slate-500">Code de suivi (à conserver)</div>
              <div className="font-mono font-bold text-lg text-[#1E3A8A]">{result.tracking_code}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => copyCode(result.tracking_code)}><Copy className="h-3 w-3 mr-1" />Copier</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
        <div>
          <Label>Catégorie *</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="sig-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Sujet (bref) *</Label>
          <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} maxLength={200} placeholder="Ex : Situation observée en récréation" data-testid="sig-subject" />
        </div>
        <div>
          <Label>Description détaillée *</Label>
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={6} maxLength={5000} placeholder="Décrivez avec précision : que s'est-il passé ? Quand ? Depuis combien de temps ?" data-testid="sig-description" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Lieu (optionnel)</Label>
            <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Ex : Cour, classe X, transport" data-testid="sig-location" />
          </div>
          <div>
            <Label>Personne mise en cause (optionnel)</Label>
            <Input value={form.reported_person} onChange={e => setForm({ ...form, reported_person: e.target.value })} placeholder="Optionnel" data-testid="sig-reported" />
          </div>
        </div>
        <Button onClick={submit} disabled={saving} className="w-full bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="sig-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" />Envoyer anonymement</>}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="font-heading font-bold text-[#1E3A8A] mb-3 flex items-center gap-2"><Search className="h-4 w-4" />Suivre un signalement</div>
        <div className="flex gap-2">
          <Input value={trackCode} onChange={e => setTrackCode(e.target.value)} placeholder="Ex : SIG-XXXXXXXX" className="font-mono" data-testid="track-code" />
          <Button variant="outline" onClick={track} data-testid="track-btn">Suivre</Button>
        </div>
        {trackResult && (
          <div className="mt-3 bg-slate-50 rounded-lg p-3 text-sm" data-testid="track-result">
            <div className="text-xs text-slate-500 mb-1">Statut : <span className="font-bold text-[#1E3A8A] uppercase">{trackResult.status}</span></div>
            <div className="text-slate-700 truncate">{trackResult.subject}</div>
            <div className="text-xs text-slate-400 mt-1">Créé le {new Date(trackResult.created_at).toLocaleDateString("fr-FR")}</div>
          </div>
        )}
      </div>
    </div>
  );
}
